import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Target, Settings, Server, HardDrive, Wifi, Shield } from 'lucide-react';

const ITInfrastrukturKMU = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'IT-Infrastruktur für KMU Mönchengladbach',
    description: 'Stabile IT-Infrastruktur für KMU in Mönchengladbach: Server, Netzwerk, Backups. Pragmatisch aufgebaut, verständlich dokumentiert.',
    provider: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' },
    areaServed: ['Mönchengladbach', 'Viersen', 'Krefeld', 'Neuss', 'Düsseldorf', 'Nordrhein-Westfalen'],
    serviceType: 'IT-Infrastruktur'
  };

  const probleme = [
    'Ihre IT läuft irgendwie, aber niemand weiß genau, was da eigentlich passiert',
    'Wenn etwas ausfällt, dauert es ewig, bis es wieder läuft',
    'Backups? Gibt es wahrscheinlich. Ob sie funktionieren? Keine Ahnung.',
    'Jeder hat sein eigenes System – keine einheitliche Struktur',
    'Der IT-Dienstleister spricht eine andere Sprache'
  ];

  const bereiche = [
    {
      icon: Server,
      title: 'Server & Cloud',
      text: 'Wo laufen Ihre Daten? Lokal, in der Cloud, hybrid? Ich schaue, was für Sie passt – und richte es vernünftig ein.'
    },
    {
      icon: Wifi,
      title: 'Netzwerk',
      text: 'WLAN, Kabel, Drucker, Scanner. Alles sollte funktionieren, ohne dass Sie nachdenken müssen.'
    },
    {
      icon: HardDrive,
      title: 'Backups',
      text: 'Regelmäßige Sicherungen sind Pflicht. Ich richte sie ein – und teste, ob sie auch wirklich funktionieren.'
    },
    {
      icon: Settings,
      title: 'Wartung',
      text: 'Updates, Patches, Überwachung. Damit Ihre IT läuft und nicht irgendwann überraschend stehen bleibt.'
    }
  ];

  const beispiele = [
    {
      title: 'Handwerksbetrieb (8 Mitarbeiter)',
      situation: 'Alter Server im Büro, keine Backups, WLAN instabil',
      loesung: 'Microsoft 365 mit Cloud-Speicher, strukturierte Ablage, neuer WLAN-Router, automatische Backups',
      ergebnis: 'Keine Server-Wartung mehr, von überall Zugriff, Daten gesichert'
    },
    {
      title: 'Steuerberater (4 Mitarbeiter)',
      situation: 'Mandantendaten auf lokalem Server, unsichere Freigaben, unklare Backup-Strategie',
      loesung: 'Dedizierter Cloud-Server, verschlüsselte Verbindungen, tägliche Backups mit Restore-Tests',
      ergebnis: 'DSGVO-konform, sicher, jederzeit wiederherstellbar'
    }
  ];

  const vorgehen = [
    'Bestandsaufnahme: Was haben Sie, wie läuft es?',
    'Schwachstellen identifizieren: Wo droht Ärger?',
    'Empfehlung: Was sollten Sie ändern, was kann bleiben?',
    'Umsetzung: Ich richte es ein oder begleite Ihren Dienstleister',
    'Dokumentation: Alles wird verständlich aufgeschrieben',
    'Wartungsplan: Was muss wann gemacht werden?'
  ];

  const relatedPages = [
    { title: 'IT-Sicherheit', url: '/it-sicherheit-isms', description: 'Ihre Infrastruktur sicher machen' },
    { title: 'Microsoft 365', url: '/microsoft-365-optimierung', description: 'Cloud-Infrastruktur optimal nutzen' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="IT-Infrastruktur für KMU in Mönchengladbach – Schteffann"
        description="Stabile IT-Infrastruktur für KMU in Mönchengladbach: Server, Netzwerk, Backups. Pragmatisch aufgebaut, verständlich dokumentiert. Raum Mönchengladbach, Viersen, Krefeld."
        keywords="IT-Infrastruktur Mönchengladbach, Server Netzwerk KMU, Backup Strategie, IT-Administration, Cloud Migration, Microsoft 365, Mönchengladbach, Viersen, Krefeld, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Settings className="w-4 h-4 mr-2" />
              IT-Infrastruktur
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">IT-Infrastruktur für KMU in </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Mönchengladbach</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Server, Netzwerk, Backups – das muss laufen. Ich bringe Struktur in Ihre IT und sorge dafür, dass Sie sich darauf verlassen können – für Unternehmen in Mönchengladbach, Viersen und Krefeld.
            </p>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Typische Situation in KMU</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {probleme.map((problem, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{problem}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-lg text-slate-700 mt-8 font-medium">
                  Das ist in vielen Unternehmen so. Muss aber nicht bleiben.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bereiche */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Die wichtigsten Bereiche
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Worauf es bei IT-Infrastruktur wirklich ankommt:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {bereiche.map((bereich, index) => (
              <Card key={index} className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl">
                      <bereich.icon className="h-6 w-6 text-cyan-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 pt-2">{bereich.title}</h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{bereich.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Beispiele */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Beispiele aus der Praxis
            </h2>
          </div>

          <div className="space-y-8 max-w-4xl mx-auto">
            {beispiele.map((beispiel, index) => (
              <Card key={index} className="border-slate-200 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">{beispiel.title}</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Ausgangssituation</h4>
                      <p className="text-slate-700">{beispiel.situation}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Lösung</h4>
                      <p className="text-slate-700">{beispiel.loesung}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Ergebnis</h4>
                      <p className="text-slate-900 font-medium">{beispiel.ergebnis}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vorgehen */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Wie ich vorgehe</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {vorgehen.map((schritt, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center text-cyan-700 font-bold">
                        {index + 1}
                      </div>
                      <p className="text-lg text-slate-700 pt-1">{schritt}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Das könnte Sie auch interessieren</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {relatedPages.map((page, index) => (
              <Link key={index} to={page.url}>
                <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-3">
                      {page.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{page.description}</p>
                    <div className="flex items-center text-cyan-600 group-hover:text-cyan-700">
                      <span className="text-sm font-medium">Mehr erfahren</span>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Bringen Sie Struktur in Ihre IT
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Lassen Sie uns schauen, was bei Ihrer IT-Infrastruktur verbessert werden sollte.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Gespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ITInfrastrukturKMU;